import { useMutation, useQuery } from '@tanstack/react-query';

import { useSurveysGateway } from './SurveysGatewayProvider';
import {
  getPublicSurveyByGroupQueryOptions,
  isSurveyAuthRequiredError,
  surveyRequiresAuthentication,
} from '../api/survey';
import type { PublicSurveyGuest } from '../model/types';

/**
 * The currently published version of a survey group, fetched without a
 * session. `enabled` lets the caller withhold the fetch, which the public
 * screen does while a session exists.
 *
 * `isAuthRequiredError` separates "sign in first" from a genuine failure, and
 * `requiresAuthentication` reports a definition that closes itself to guests.
 */
export function usePublicSurvey(surveyGroupId: string, { enabled }: { enabled: boolean }) {
  const gateway = useSurveysGateway();

  const queryOptions = getPublicSurveyByGroupQueryOptions(surveyGroupId, gateway);

  const query = useQuery({
    ...queryOptions,
    enabled: queryOptions.enabled !== false && enabled,
  });

  return {
    ...query,
    requiresAuthentication: surveyRequiresAuthentication(query.data),
    isAuthRequiredError: query.isError && isSurveyAuthRequiredError(query.error),
  };
}

export function useSubmitPublicSurveyResponse(surveyId: string | undefined) {
  const gateway = useSurveysGateway();

  return useMutation({
    mutationFn: ({
      guest,
      answers,
    }: {
      guest: PublicSurveyGuest;
      answers: Record<string, unknown>;
    }) => {
      if (!surveyId) {
        return Promise.reject(new Error('Survey not loaded'));
      }

      return gateway.submitPublicResponse(surveyId, guest, answers);
    },
  });
}
